import _defineProperty from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _asyncToGenerator from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import _regeneratorRuntime from "/jove-quote/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import { useCallback, useMemo, useState } from 'react';
import { Auth0Context, initialContext } from '@auth0/auth0-react';
import { removeAllAuthKeys } from '../../utils/auth';
import { clearJoveAuthSession, loadJoveAuthSession } from '../../utils/joveAuthSession';
import { jsx as _jsx } from "react/jsx-runtime";

/**
 * Auth0Context-compatible provider backed by the backend-issued Jove auth session
 * (see utils/joveAuthSession). Drop-in replacement for Auth0Provider: existing useAuth0()
 * call sites keep working, but login/logout go through the app-local login page instead
 * of the Auth0 hosted flow, and the session is shared across all Jove apps.
 */
export var JoveAuthProvider = function JoveAuthProvider(_ref) {
  var children = _ref.children,
      buildLoginPageUrl = _ref.buildLoginPageUrl;

  var _useState = useState(function () {
    return loadJoveAuthSession();
  }),
      session = _useState[0],
      setSession = _useState[1];
  /**
   * methods
   */


  var getAccessTokenSilently = useCallback( /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(options) {
      var currentSession;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              currentSession = loadJoveAuthSession();

              if (currentSession) {
                _context.next = 3;
                break;
              }

              throw new Error('Login required');

            case 3:
              if (!(options !== null && options !== void 0 && options.detailedResponse)) {
                _context.next = 5;
                break;
              }

              return _context.abrupt("return", {
                id_token: currentSession.idToken,
                access_token: currentSession.accessToken,
                expires_in: Math.max(0, Math.floor((currentSession.expiresAt - Date.now()) / 1000))
              });

            case 5:
              return _context.abrupt("return", currentSession.accessToken);

            case 6:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function (_x) {
      return _ref2.apply(this, arguments);
    };
  }(), []);
  var getIdTokenClaims = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
    var currentSession;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            currentSession = loadJoveAuthSession();
            return _context2.abrupt("return", currentSession ? _objectSpread({
              __raw: currentSession.idToken
            }, currentSession.user || {}) : undefined);

          case 2:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  })), []);
  var loginWithRedirect = useCallback( /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(options) {
      var _options$appState;

      var returnTo;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              returnTo = options === null || options === void 0 ? void 0 : (_options$appState = options.appState) === null || _options$appState === void 0 ? void 0 : _options$appState.returnTo;

              if (returnTo) {
                window.sessionStorage.setItem('jove-returnTo', returnTo);
              }

              window.location.assign(buildLoginPageUrl());

            case 3:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    return function (_x2) {
      return _ref4.apply(this, arguments);
    };
  }(), [buildLoginPageUrl]);
  var logout = useCallback( /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(options) {
      var openUrl;
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              clearJoveAuthSession();
              removeAllAuthKeys();
              setSession(null); // auth0 contract: openUrl takes over the post-logout navigation (used by the invitation pages to stay in place)

              openUrl = options === null || options === void 0 ? void 0 : options.openUrl;

              if (!(openUrl === false)) {
                _context4.next = 6;
                break;
              }

              return _context4.abrupt("return");

            case 6:
              if (!(typeof openUrl === 'function')) {
                _context4.next = 9;
                break;
              }

              openUrl(buildLoginPageUrl());
              return _context4.abrupt("return");

            case 9:
              window.location.assign(buildLoginPageUrl());

            case 10:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));

    return function (_x3) {
      return _ref5.apply(this, arguments);
    };
  }(), [buildLoginPageUrl]);
  var contextValue = useMemo(function () {
    return _objectSpread(_objectSpread({}, initialContext), {}, {
      isAuthenticated: !!session,
      isLoading: false,
      error: undefined,
      user: session === null || session === void 0 ? void 0 : session.user,
      getAccessTokenSilently: getAccessTokenSilently,
      getIdTokenClaims: getIdTokenClaims,
      loginWithRedirect: loginWithRedirect,
      logout: logout
    });
  }, [session, getAccessTokenSilently, getIdTokenClaims, loginWithRedirect, logout]);
  return /*#__PURE__*/_jsx(Auth0Context.Provider, {
    value: contextValue,
    children: children
  });
};
export default JoveAuthProvider;